import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { clearScope } from '../cache/cachePublicMethods';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.menu_api.categories;


// Helper function to configure the Axios instance
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'menu_api',
  group: 'categories',
  defaultTtl: '15m',
  hashLargeSegments: true,
  emitMeta: true,
});


//GetMenuCategoriesByLocalId
export const getMenuCategoriesByLocalId = async (localId: number) => {
  // Use cached GET; key is built automatically from method+URL+params
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetMenuCategoriesByLocalId/${localId}`)
  );
};

/**
 * Fetch all menu categories (parent and child) for a given local ID.
 * GET /GetAllMenuCategoriesParentAndChildByLocalId/<local_id>
 */
export const getAllMenuCategoriesParentAndChildByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetAllMenuCategoriesParentAndChildByLocalId/${localId}`)
  );
};

export const toggleIsMainCategory = async (categoryId: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/ToggleIsMainCategory/${categoryId}`)
  );
  await cachedApi.clear();
  return res;
};


export const updateWorkstationOfCategory = async (category_id: number, workstation_id: number | null) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateWorkstationOfCategory/${category_id}/${workstation_id}`)
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};

export const getMenuCategoriesById = async (category_id: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetMenuCategoriesById/${category_id}`)
  );
};

// createCategory
export const createCategory = async (categoryData: any) => {
  const res = await retryWithBackoff(() =>
    api.post('/CreateMenuCategory', categoryData)
  );
  // Invalidate cache scopes after mutation
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};

  // updateCategory
export const updateCategory = async (category_id: number, categoryData: any) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateMenuCategoryById/${category_id}`, categoryData)
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};

// deleteCategory
export const deleteCategory = async (category_id: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteMenuCategoryById/${category_id}`)
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};

// /GetAllMenuCategoriesByMenuId/<int:menu_id>

export const getAllMenuCategoriesByMenuId = async (menu_id: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetAllMenuCategoriesByMenuId/${menu_id}`)
  );
};


//Get All Menu Parent Categories By LocalId
export const getAllParentsCategoriesByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetAllParentsCategoriesByLocalId/${localId}`)
  );
};

//Get All Sub-cate By CategoryId (Parent Category)
export const getSubCategoriesByCategoryId = async (category_id: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetSubCategoriesByCategoryId/${category_id}`)
  );
};


//Add a subcategory to an category
export const addSubCategoryToCategory = async (category_id: number, sub_category_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/AddSubCategoryToCategory/${category_id}/${sub_category_id}`)
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};


//Add
export const deleteSubCategoryFromCategory = async (sub_category_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/DeleteSubCategoryFromCategory/${sub_category_id}`)
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};


//Get All Menu Parent Categories (With no subcategory) By LocalId
export const getCandidatesToSubcategory = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetCandidatesToSubcategory/${localId}`)
  );
};

/**
 * Get all candidate objects to be subcategories for a given local.
 * GET /GetCandidatesObjectsToSubcategory/<local_id>
 */
export const getCandidatesObjectsToSubcategory = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetCandidatesObjectsToSubcategory/${localId}`)
  );
};


export const getAllItemHolderCategoriesByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetAllItemHolderCategoriesByLocalId/${localId}`)
  );
};

/**
 * # ═══════════════════════════════════════════════════════════════════════════════

move_category_position_parser = reqparse.RequestParser()
move_category_position_parser.add_argument('move_up', type=bool, required=True, 
    help='True to move category up (decrease position), False to move down', location='json')

@ns.route('/MoveCategoryPosition/<int:menu_id>/<int:category_id>')
@ns.param('menu_id', 'The menu identifier')
@ns.param('category_id', 'The category identifier')
class MoveCategoryPosition(Resource):
    @ns.doc('move_category_position', security='bearerAuth')
    @ns.expect(move_category_position_parser)
    @jwt_required()
    def put(self, menu_id, category_id):
        '''Move a category up or down within a menu'''
        data = move_category_position_parser.parse_args()
        move_up = data['move_up']
        
        # Get all category mappings for this menu, ordered by position
        mappings = MenuCategoryMapping.query.filter_by(menu_id=menu_id).order_by(
            MenuCategoryMapping.position.asc().nullslast()
        ).all()
        
        if not mappings:
            return {"message": "No se encontraron categorías en este menú"}, 404
        
        # Find the target category mapping
        target_mapping = None
        target_index = None
        for idx, mapping in enumerate(mappings):
            if mapping.category_id == category_id:
                target_mapping = mapping
                target_index = idx
                break
        
        if target_mapping is None:
            return {"message": "La categoría no pertenece a este menú"}, 404
        
        # Initialize positions if they're all NULL (first time ordering)
        if all(m.position is None for m in mappings):
            for idx, mapping in enumerate(mappings):
                mapping.position = idx + 1
            db.session.flush()
        
        # Calculate swap index
        if move_up:
            if target_index == 0:
                return {"message": "La categoría ya está en la primera posición"}, 400
            swap_index = target_index - 1
        else:
            if target_index == len(mappings) - 1:
                return {"message": "La categoría ya está en la última posición"}, 400
            swap_index = target_index + 1
        
        swap_mapping = mappings[swap_index]
        
        # Swap positions
        target_position = target_mapping.position
        swap_position = swap_mapping.position
        
        target_mapping.position = swap_position
        swap_mapping.position = target_position
        
        db.session.commit()
        
        return {
            "message": "Posición actualizada exitosamente",
            "category_id": category_id,
            "new_position": target_mapping.position,
            "swapped_with_category_id": swap_mapping.category_id
        }, 200


set_category_position_parser = reqparse.RequestParser()
set_category_position_parser.add_argument('position', type=int, required=True, 
    help='The new position for the category', location='json')

@ns.route('/SetCategoryPosition/<int:menu_id>/<int:category_id>')
@ns.param('menu_id', 'The menu identifier')
@ns.param('category_id', 'The category identifier')
class SetCategoryPosition(Resource):
    @ns.doc('set_category_position', security='bearerAuth')
    @ns.expect(set_category_position_parser)
    @jwt_required()
    def put(self, menu_id, category_id):
        '''Set the position of a category within a menu'''
        data = set_category_position_parser.parse_args()
        position = data['position']
        
        mapping = MenuCategoryMapping.query.filter_by(
            menu_id=menu_id, 
            category_id=category_id
        ).first()
        
        if not mapping:
            return {"message": "La categoría no pertenece a este menú"}, 404
        
        mapping.position = position
        db.session.commit()
        return {"message": "Posición actualizada", "position": position}, 200


reorder_categories_parser = reqparse.RequestParser()
reorder_categories_parser.add_argument('category_order', type=list, required=True, 
    help='List of category IDs in desired order', location='json')

@ns.route('/ReorderMenuCategories/<int:menu_id>')
@ns.param('menu_id', 'The menu identifier')
class ReorderMenuCategories(Resource):
    @ns.doc('reorder_menu_categories', security='bearerAuth')
    @ns.expect(reorder_categories_parser)
    @jwt_required()
    def put(self, menu_id):
        '''Reorder all categories in a menu'''
        data = reorder_categories_parser.parse_args()
        category_order = data['category_order']
        
        if not isinstance(category_order, list):
            return {'message': 'category_order debe ser una lista de IDs'}, 400
        
        mappings = {m.category_id: m for m in MenuCategoryMapping.query.filter_by(menu_id=menu_id).all()}
        
        if not mappings:
            return {"message": "No se encontraron categorías en este menú"}, 404
        
        # Validate all category IDs exist in the menu
        for cat_id in category_order:
            if cat_id not in mappings:
                return {"message": f"La categoría {cat_id} no pertenece a este menú"}, 400
        
        # Update positions
        for position, category_id in enumerate(category_order, start=1):
            mappings[category_id].position = position
        
        db.session.commit()
        
        return {"message": "Categorías reordenadas exitosamente"}, 200
 */

export const moveCategoryPosition = async (menu_id: number, category_id: number, move_up: boolean) => {
  const res = await retryWithBackoff(() =>
    api.put(`/MoveCategoryPosition/${menu_id}/${category_id}`, { move_up })
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};

export const setCategoryPosition = async (menu_id: number, category_id: number, position: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/SetCategoryPosition/${menu_id}/${category_id}`, { position })
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};

export const reorderMenuCategories = async (menu_id: number, category_order: number[]) => {
  const res = await retryWithBackoff(() =>
    api.put(`/ReorderMenuCategories/${menu_id}`, { category_order })
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};

/**
 * @ns.route('/NormalizeMenuCategoryPositions/<int:menu_id>')
@ns.param('menu_id', 'The menu identifier')
class NormalizeMenuCategoryPositions(Resource):
    @ns.doc('normalize_menu_category_positions', security='bearerAuth')
    @jwt_required()
    def put(self, menu_id):
        '''Normalize positions at all levels: root categories and each parent's subcategories'''
        try:
            changes_made = 0
            normalized_levels = []
            
            # 1. Normalize ROOT categories (is_main=True, parent_category_id=NULL)
            root_mappings = MenuCategoryMapping.query.join(MenuCategory).filter(
                MenuCategoryMapping.menu_id == menu_id,
                MenuCategory.is_main == True,
                MenuCategory.parent_category_id.is_(None)
            ).order_by(
                db.case((MenuCategoryMapping.position.is_(None), 1), else_=0),
                MenuCategoryMapping.position.asc(),
                MenuCategoryMapping.category_id.asc()
            ).all()
            
            for idx, mapping in enumerate(root_mappings):
                new_pos = idx + 1
                if mapping.position != new_pos:
                    mapping.position = new_pos
                    changes_made += 1
            
            normalized_levels.append({
                "level": "root",
                "parent_category_id": None,
                "count": len(root_mappings),
                "positions": [{"category_id": m.category_id, "position": m.position} for m in root_mappings]
            })
            
            # 2. Find all unique parent_category_ids in this menu
            parent_ids_query = db.session.query(MenuCategory.parent_category_id).join(
                MenuCategoryMapping,
                MenuCategory.category_id == MenuCategoryMapping.category_id
            ).filter(
                MenuCategoryMapping.menu_id == menu_id,
                MenuCategory.parent_category_id.isnot(None)
            ).distinct().all()
            
            parent_ids = [p[0] for p in parent_ids_query]
            
            # 3. Normalize subcategories for each parent
            for parent_id in parent_ids:
                sub_mappings = MenuCategoryMapping.query.join(MenuCategory).filter(
                    MenuCategoryMapping.menu_id == menu_id,
                    MenuCategory.parent_category_id == parent_id
                ).order_by(
                    db.case((MenuCategoryMapping.position.is_(None), 1), else_=0),
                    MenuCategoryMapping.position.asc(),
                    MenuCategoryMapping.category_id.asc()
                ).all()
                
                for idx, mapping in enumerate(sub_mappings):
                    new_pos = idx + 1
                    if mapping.position != new_pos:
                        mapping.position = new_pos
                        changes_made += 1
                
                normalized_levels.append({
                    "level": "subcategory",
                    "parent_category_id": parent_id,
                    "count": len(sub_mappings),
                    "positions": [{"category_id": m.category_id, "position": m.position} for m in sub_mappings]
                })
            
            # 4. Set NULL for categories that are non-main AND have no parent (orphans shouldn't have positions)
            orphan_mappings = MenuCategoryMapping.query.join(MenuCategory).filter(
                MenuCategoryMapping.menu_id == menu_id,
                MenuCategory.is_main == False,
                MenuCategory.parent_category_id.is_(None)
            ).all()
            
            for mapping in orphan_mappings:
                if mapping.position is not None:
                    mapping.position = None
                    changes_made += 1
            
            db.session.commit()
            
            return {
                "message": "Posiciones normalizadas exitosamente",
                "changes_made": changes_made,
                "levels": normalized_levels
            }, 200
            
        except Exception as e:
            db.session.rollback()
            return {"message": f"Error al normalizar posiciones: {str(e)}"}, 500
 */

export const normalizeMenuCategoryPositions = async (menu_id: number, parent_category_id?: number | null) => {
  const payload = parent_category_id !== undefined && parent_category_id !== null 
    ? { parent_category_id } 
    : {};
  const res = await retryWithBackoff(() =>
    api.put(`/NormalizeMenuCategoryPositions/${menu_id}`, payload)
  );
  await clearScope({ namespace: 'menu_api', group: 'categories' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};